import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { cn, mapEdgesToNodes, imageBuilder, toPlainText } from "../../../lib/helpers";
import { graphql, useStaticQuery } from "gatsby";
import PortableText from "../../portableText";
import { SectionHeaders } from "../wrapper";
import { ImageBuilder } from "../../global/image-builder";
import { pageDocPath } from "../../../../helpers";
import { TinyPills } from "../../card/card";
import { faStar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// @todo change link ternary to status (isPublished) rather than _rawBody length

const LOGO_IMAGE_HEIGHT = 32;

const generateIntegrationsListSchema = (integrations) => {
  if (!integrations || integrations.length === 0) return null;

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": integrations.map((doc, index) => {
      const item = {
        "@type": "SoftwareApplication",
        "name": doc.company,
        "applicationCategory": doc.category || "BusinessApplication",
      };

      if (doc.mainImage?.asset) {
        const logoUrl = imageBuilder(doc.mainImage)?.height(200)?.url();
        if (logoUrl) item.image = logoUrl;
      }

      if (doc._rawExcerpt) {
        const description = toPlainText(doc._rawExcerpt);
        if (description) item.offers = description;
      }

      return {
        "@type": "ListItem",
        "position": index + 1,
        "item": item,
      };
    }),
  };

  return schema;
};

export const CATEGORIES = [
  {
    label: "Business",
    types: ["gtm"],
  },
  {
    label: "Product",
    types: ["product"],
  },
  {
    label: "Social & community",
    types: ["social", "code", "community"],
  },
  {
    label: "Custom",
    types: ["custom"],
  },
];

export const Integrations = ({ section }) => {
  const [sourceTypeKey, setSourceTypeKey] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState(null);
  const [planFilter, setPlanFilter] = useState(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { docs } =
    useStaticQuery(graphql`
      query IntegrationsSectionQuery {
        docs: allSanityIntegration(
          sort: { fields: [company], order: ASC }
          filter: { status: { in: ["published", "hidden"] } }
        ) {
          edges {
            node {
              _type
              _id
              status
              company
              _rawExcerpt
              category
              plans
              isFeatured
              mainImage {
                ...SanityImageSimple
              }
              orderRank
              purchase
              purchaseStarter
              purchaseEnterprise
              _rawBody(resolveReferences: { maxDepth: 12 })
              partner
              sourceType
              docRef {
                _id
                _type
                slug {
                  current
                }
                menu
                menuParent
              }
            }
          }
        }
      }
    `) || {};

  const docList = docs ? mapEdgesToNodes(docs) : [];
  const integrationsListSchema = generateIntegrationsListSchema(docList);

  const selectedSourceTypes = useMemo(
    () => CATEGORIES.find((c) => c.label === sourceTypeKey)?.types || null,
    [sourceTypeKey]
  );

  const uniqueCategories = useMemo(
    () => [...new Set(docList.map((d) => d.category).filter(Boolean))].sort(),
    [docList]
  );

  const activeFilterCount = [sourceTypeKey, planFilter].filter(Boolean).length;

  const filtered = docList
    .filter((doc) => !selectedSourceTypes || (doc.sourceType && selectedSourceTypes.includes(doc.sourceType)))
    .filter((doc) => !categoryFilter || doc.category === categoryFilter)
    .filter((doc) => {
      if (!planFilter) return true;
      if (planFilter === "Essential") return (doc.plans && doc.plans.includes("starter")) || doc.purchaseStarter;
      if (planFilter === "Advanced") return (doc.plans && doc.plans.includes("team")) || doc.purchase;
      if (planFilter === "Enterprise") return true;
      return true;
    })
    .filter((doc) => !searchQuery || doc.company.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => b.isFeatured - a.isFeatured);

  return (
    <>
      {integrationsListSchema && (
        <Helmet
          script={[{
            type: "application/ld+json",
            innerHTML: JSON.stringify(integrationsListSchema),
          }]}
        />
      )}
      <SectionHeaders {...{ ...section, ...{ _rawSummary: [] } }} />

      <div className="md:hidden mb-4">
        <button
          onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-dark-5 text-dark-80 font-medium hover:bg-dark-10"
        >
          Filters
          {activeFilterCount > 0 && (
            <span className="text-xs bg-green-emerald text-white rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${mobileFiltersOpen ? "max-h-screen opacity-100 mt-4" : "max-h-0 opacity-0"}`}>
          <div className="flex flex-col gap-6">
            <FilterGroup
              label="Signals"
              options={CATEGORIES.map((c) => c.label)}
              active={sourceTypeKey}
              onSelect={(val) => { setCategoryFilter(null); setPlanFilter(null); setSourceTypeKey(val === sourceTypeKey ? null : val); }}
            />
            <FilterGroup
              label="Pricing Plan"
              options={["Starter", "Team", "Enterprise"]}
              active={planFilter}
              onSelect={(val) => { setSourceTypeKey(null); setCategoryFilter(null); setPlanFilter(val === planFilter ? null : val); }}
            />
          </div>
        </div>
      </div>

      <div className="flex gap-8">
        <aside className="hidden md:block w-44 flex-shrink-0 space-y-6 sticky top-24 self-start">
          <FilterGroup
            label="Signals"
            options={CATEGORIES.map((c) => c.label)}
            active={sourceTypeKey}
            onSelect={(val) => { setCategoryFilter(null); setPlanFilter(null); setSourceTypeKey(val === sourceTypeKey ? null : val); }}
          />
          {/* <FilterGroup
            label="Data Source"
            options={uniqueCategories}
            active={categoryFilter}
            onSelect={(val) => { setSourceTypeKey(null); setPlanFilter(null); setCategoryFilter(val === categoryFilter ? null : val); }}
          /> */}
          <FilterGroup
            label="Pricing Plan"
            options={["Essential", "Advanced", "Enterprise"]}
            active={planFilter}
            onSelect={(val) => { setSourceTypeKey(null); setCategoryFilter(null); setPlanFilter(val === planFilter ? null : val); }}
          />
        </aside>

        <div className="flex-1">
          <input
            type="search"
            placeholder="Search integrations…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full mb-4 px-4 py-2 rounded-lg bg-dark-5 text-dark-80 placeholder-dark-40 border border-transparent focus:outline-none focus:border-link"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {filtered.map((doc) => (
              <CardIntegration doc={doc} key={doc._id} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

const FilterGroup = ({ label, options, active, onSelect }) => (
  <div>
    <p className="text-sm font-semibold uppercase tracking-wide text-dark-50 mb-2">{label}</p>
    <ul className="space-y-1">
      {options.map((opt) => (
        <li key={opt}>
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); onSelect(opt); }}
            className={cn(
              "block p-3 rounded-lg",
              active === opt ? "bg-dark-20 text-black font-medium" : "bg-dark-5 text-dark-80 hover:bg-dark-10 hover:text-black hover:font-medium"
            )}
          >
            {opt}
          </a>
        </li>
      ))}
    </ul>
  </div>
);

export const CardIntegration = ({ doc }) => {
  return (
    <a
      href={doc.docRef ? pageDocPath(doc.docRef) : undefined}
      target={doc.docRef ? "_blank" : undefined}
      rel={doc.docRef ? "noopener noreferrer" : undefined}
      className="relative rounded-xl p-3 sm:p-5 md:p-4 md:pb-5 bg-white border border-white hover:border-green-emerald hover:shadow-md scale-100 hover:scale-102 transform transition-all duration-200 ease-in-out"
    >
      {doc.isFeatured && (
        <span className="absolute top-3 right-3 text-xs font-medium px-1 py-0.5 rounded-full border border-green-emerald text-green-emerald">
          <FontAwesomeIcon icon={faStar} />
        </span>
      )}
      <CardContent doc={doc} />
    </a>
  );
};

const getPlanPills = (doc) => {
  const plans = doc.plans || [];

  const pills = [];
  if (!plans.includes("starter")) {
    if (doc.purchase || doc.purchaseStarter || doc.purchaseEnterprise) {
      if (doc.purchaseStarter) pills.push("starter add-on");
      if (doc.purchase) pills.push("team add-on");
      if (doc.purchaseEnterprise) pills.push("enterprise add-on");
    } else {
      pills.push(plans.includes("team") ? "team" : "enterprise");
    }
  }

  return pills;
};

const CardContent = ({ doc }) => (
  <div className="text-xs flex flex-col h-full">
    <div className="flex sm:block">
      <div className="mr-2 md:mr-4 pt-1 sm:mb-3">
        <CompanyLogo image={doc.mainImage} alt={`${doc.company} logo`} />
      </div>
      <div className="mb-2">
        <h4 className="font-semibold text-xl">{doc.company}</h4>
        <p className="text-dark-50 sm:hidden">{doc.category}</p>
        {doc.partner && (
          <p className="my-3 px-2 py-1 rounded-md bg-dark-5 text-xs inline-block">
            Data via partner
          </p>
        )}
      </div>
    </div>
    <div className="flex-grow text-dark-80 leading-tight mb-2">
      <PortableText blocks={doc._rawExcerpt} />
    </div>
    {/* {getPlanPills(doc).length > 0 && (<div className="flex flex-wrap my-2 -ml-1">
      <TinyPills list={getPlanPills(doc)} />
    </div>)} */}
    <div className="mt-2 -ml-1">
      <p className="text-dark-60 inline px-2 py-1 rounded-md bg-dark-5" id="categoryLabel">
        {doc.category}
      </p>
    </div>
  </div>
);

const CompanyLogo = ({ image, alt }) =>
  image ? (
    <ImageBuilder
      image={image}
      height={LOGO_IMAGE_HEIGHT}
      width={Math.round(
        LOGO_IMAGE_HEIGHT * image.asset.metadata.dimensions.aspectRatio
      )}
      quality={100}
      style={{
        maxHeight: LOGO_IMAGE_HEIGHT,
        maxWidth: LOGO_IMAGE_HEIGHT * 1.5,
      }}
      alt={alt || image.alt}
    />
  ) : null;
